import React from 'react';
import { osc, wobble, lerp, loop, clamp, easeOut } from '../../core/AnimationUtils';

/*
 * Concept-acting SCENES (Anivaze v2) — the concept HAPPENS in front of you
 * instead of being labelled in boxes. Kindergarten-friendly, pure function of
 * `t` (deterministic + exportable). Reference for the scene library; see
 * design-plan/v2/ANIMATION-PATTERN.md.
 */

const polar = (cx: number, cy: number, r: number, deg: number): [number, number] => {
  const a = (deg * Math.PI) / 180;
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
};

/** A spinning cartoon wheel. */
const Wheel = (wx: number, wy: number, r: number, ang: number) => (
  <g key={`w${wx}`}>
    <circle cx={wx} cy={wy} r={r} fill="#111827" />
    <circle cx={wx} cy={wy} r={r} fill="none" stroke="#374151" strokeWidth={3} />
    <g transform={`rotate(${ang} ${wx} ${wy})`}>
      {Array.from({ length: 6 }, (_, k) => {
        const a = (k / 6) * Math.PI * 2;
        return <line key={k} x1={wx} y1={wy} x2={wx + (r - 4) * Math.cos(a)} y2={wy + (r - 4) * Math.sin(a)} stroke="#9ca3af" strokeWidth={2.5} />;
      })}
      <circle cx={wx} cy={wy} r={r * 0.32} fill="#e5e7eb" />
    </g>
  </g>
);

/** A cute car body centred at (cx, cy). */
const Car = (cx: number, cy: number, accent: string, wheelAng: number) => (
  <g>
    <ellipse cx={cx} cy={cy + 20} rx={56} ry={7} fill="rgba(0,0,0,0.18)" />
    <path d={`M${cx - 56},${cy + 6} Q${cx - 58},${cy - 12} ${cx - 40},${cy - 14} L${cx + 44},${cy - 14} Q${cx + 58},${cy - 14} ${cx + 58},${cy + 6} Z`} fill={accent} stroke="#7f1d1d" strokeWidth={2} />
    <path d={`M${cx - 26},${cy - 13} Q${cx - 20},${cy - 34} ${cx - 2},${cy - 34} L${cx + 20},${cy - 34} Q${cx + 30},${cy - 33} ${cx + 34},${cy - 13} Z`} fill="#fca5a5" stroke="#7f1d1d" strokeWidth={2} />
    <path d={`M${cx - 18},${cy - 14} Q${cx - 13},${cy - 28} ${cx - 1},${cy - 28} L${cx + 14},${cy - 28} Q${cx + 22},${cy - 27} ${cx + 25},${cy - 14} Z`} fill="#bae6fd" />
    <circle cx={cx + 4} cy={cy - 20} r={3.4} fill="#1f2937" />
    <circle cx={cx + 16} cy={cy - 20} r={3.4} fill="#1f2937" />
    <circle cx={cx + 52} cy={cy - 2} r={4.5} fill="#fde68a" stroke="#b45309" strokeWidth={1} />
    <path d={`M${cx - 12},${cy + 2} Q${cx},${cy + 9} ${cx + 12},${cy + 2}`} fill="none" stroke="#7f1d1d" strokeWidth={2} strokeLinecap="round" />
    {Wheel(cx - 30, cy + 16, 15, wheelAng)}
    {Wheel(cx + 32, cy + 16, 15, wheelAng)}
  </g>
);

/* ━━━ Speed: the world scrolls past a car driving FOREVER ━━━━━━━━━━━━━━━━━━━ */
export interface CarSpeedSceneProps {
  opacity?: number;
  accentColor?: string;
  label?: string;
  t?: number;
}

export const CarSpeedScene: React.FC<CarSpeedSceneProps> = ({
  opacity = 1, accentColor = '#ef4444', t = 0,
  label = 'Speed = how much ground you cover in a little time',
}) => {
  const roadY = 348;
  const worldSpeed = 150;                 // px/s the world slides left → motion
  const carCX = 280;
  const carCY = roadY + 22 + wobble(t, 0.3, 1.2);
  const wheelAng = (t / 0.4) * 360;
  const dash = (t * worldSpeed) % 64;
  const distance = Math.floor(t * 8);     // odometer metres, always climbing
  const stopHand = ((t / 4) * 360) % 360; // stopwatch second hand

  // Speedometer (bottom-right): top-arc gauge 180°→360°, needle stays inside it.
  const gx = 712, gy = 392, R = 40;
  const speedFrac = clamp(0.72 + osc(t, 1.4, -0.05, 0.05), 0, 1);
  const needleAng = 180 + speedFrac * 180;
  const [nx, ny] = polar(gx, gy, R * 0.74, needleAng);
  const arcBg: string[] = [], arcFill: string[] = [];
  for (let d = 180; d <= 360; d += 5) {
    const [x, y] = polar(gx, gy, R, d);
    arcBg.push(`${x},${y}`);
    if (d <= needleAng) arcFill.push(`${x},${y}`);
  }

  // Seamless scrolling roadside (trees + lamp-posts) — wraps, never resets.
  const spacing = 175, wrap = 6 * spacing;
  const roadside = Array.from({ length: 6 }, (_, i) => {
    const x = (((i * spacing - (t * worldSpeed) % wrap) + wrap) % wrap) - spacing / 2;
    return { x, kind: i % 2 };
  });

  return (
    <g opacity={opacity}>
      <defs>
        <linearGradient id="cs-sky" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#bae6fd" /><stop offset="100%" stopColor="#e0f2fe" /></linearGradient>
        <radialGradient id="cs-sun" cx="50%" cy="50%"><stop offset="0%" stopColor="#fff7cc" /><stop offset="70%" stopColor="#fbbf24" /><stop offset="100%" stopColor="#f59e0b" /></radialGradient>
      </defs>

      <rect x={0} y={0} width={800} height={roadY + 14} fill="url(#cs-sky)" />
      <rect x={0} y={roadY + 14} width={800} height={450 - (roadY + 14)} fill="#86efac" />

      {/* Sun */}
      <g transform="translate(120 78)">
        <g transform={`rotate(${(t * 8) % 360})`} opacity={0.9}>
          {Array.from({ length: 12 }, (_, k) => {
            const a = (k / 12) * Math.PI * 2;
            return <line key={k} x1={36 * Math.cos(a)} y1={36 * Math.sin(a)} x2={50 * Math.cos(a)} y2={50 * Math.sin(a)} stroke="#fcd34d" strokeWidth={3} strokeLinecap="round" />;
          })}
        </g>
        <circle r={28} fill="url(#cs-sun)" />
      </g>

      {/* Drifting clouds */}
      {[{ y: 70, s: 1 }, { y: 120, s: 0.8 }].map((c, i) => {
        const x = ((900 - (t * (18 + i * 8)) % 980) + i * 300) % 980 - 90;
        return (
          <g key={i} transform={`translate(${x} ${c.y}) scale(${c.s})`} opacity={0.95}>
            <ellipse cx={0} cy={0} rx={34} ry={22} fill="#fff" />
            <ellipse cx={34} cy={6} rx={28} ry={18} fill="#fff" />
            <ellipse cx={-30} cy={8} rx={24} ry={16} fill="#fff" />
          </g>
        );
      })}

      {/* Scrolling roadside */}
      {roadside.map((r, i) => (
        <g key={i} transform={`translate(${r.x} ${roadY})`}>
          {r.kind === 0 ? (
            <g>
              <rect x={-4} y={-44} width={8} height={44} fill="#92400e" />
              <circle cx={0} cy={-52} r={22} fill="#16a34a" />
              <circle cx={-14} cy={-44} r={15} fill="#22c55e" />
              <circle cx={14} cy={-44} r={15} fill="#22c55e" />
            </g>
          ) : (
            <g>
              <rect x={-2.5} y={-66} width={5} height={66} fill="#64748b" />
              <rect x={-2.5} y={-66} width={26} height={5} fill="#64748b" />
              <circle cx={24} cy={-62} r={6} fill="#fde68a" />
            </g>
          )}
        </g>
      ))}

      {/* Road */}
      <rect x={0} y={roadY + 14} width={800} height={48} fill="#334155" />
      <rect x={0} y={roadY + 14} width={800} height={4} fill="#475569" />
      {Array.from({ length: 16 }, (_, i) => (
        <rect key={i} x={i * 64 - dash} y={roadY + 36} width={34} height={5} rx={2.5} fill="#fde047" opacity={0.9} />
      ))}

      {/* Speed lines behind the car */}
      {Array.from({ length: 4 }, (_, k) => (
        <line key={k} x1={carCX - 64 - k * 18} y1={carCY - 8 + k * 7} x2={carCX - 38 - k * 18} y2={carCY - 8 + k * 7}
          stroke="#fff" strokeWidth={3} strokeLinecap="round" opacity={clamp(osc(t, 0.45, 0.15, 0.7, k), 0, 1)} />
      ))}

      {Car(carCX, carCY, accentColor, wheelAng)}

      {/* Stopwatch (time ticks on) */}
      <g transform="translate(560 80)">
        <rect x={-8} y={-50} width={16} height={9} rx={3} fill="#475569" />
        <circle r={36} fill="#fff" stroke="#334155" strokeWidth={5} />
        {Array.from({ length: 12 }, (_, k) => {
          const [x1, y1] = polar(0, 0, 28, k * 30);
          const [x2, y2] = polar(0, 0, 33, k * 30);
          return <line key={k} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#94a3b8" strokeWidth={2} />;
        })}
        <g transform={`rotate(${stopHand})`}><line x1={0} y1={6} x2={0} y2={-27} stroke="#ef4444" strokeWidth={3} strokeLinecap="round" /></g>
        <circle r={4} fill="#334155" />
        <text x={0} y={56} textAnchor="middle" fill="#334155" fontSize={12} fontWeight={700} fontFamily="Inter,sans-serif">Time ticking</text>
      </g>

      {/* Odometer — distance keeps climbing */}
      <g transform="translate(648 30)">
        <rect x={0} y={0} width={132} height={34} rx={8} fill="rgba(255,255,255,0.9)" stroke="#cbd5e1" strokeWidth={1.5} />
        <text x={12} y={22} fill="#334155" fontSize={13} fontWeight={600} fontFamily="Inter,sans-serif">Distance:</text>
        <text x={120} y={22} textAnchor="end" fill="#ef4444" fontSize={16} fontWeight={800} fontFamily="Inter,sans-serif">{distance} m</text>
      </g>

      {/* Speedometer (clean, needle stays inside the arc) */}
      <g>
        <polyline points={arcBg.join(' ')} fill="none" stroke="#cbd5e1" strokeWidth={8} strokeLinecap="round" />
        <polyline points={arcFill.join(' ')} fill="none" stroke="#22c55e" strokeWidth={8} strokeLinecap="round" />
        <line x1={gx} y1={gy} x2={nx} y2={ny} stroke="#ef4444" strokeWidth={3.5} strokeLinecap="round" />
        <circle cx={gx} cy={gy} r={5} fill="#334155" />
        <text x={gx} y={gy + 18} textAnchor="middle" fill="#334155" fontSize={12} fontWeight={700} fontFamily="Inter,sans-serif">Speed</text>
      </g>

      {/* Friendly caption */}
      <g transform="translate(400 36)">
        <rect x={-250} y={-22} width={500} height={40} rx={20} fill="rgba(255,255,255,0.88)" stroke="#bae6fd" strokeWidth={2} />
        <text x={0} y={4} textAnchor="middle" fill="#0f172a" fontSize={16} fontWeight={700} fontFamily="Inter,sans-serif">{label}</text>
      </g>
    </g>
  );
};

/* ━━━ Fast vs slow: same time, the faster one covers more ground ━━━━━━━━━━━━ */
export interface SpeedCompareSceneProps {
  opacity?: number;
  accentColor?: string;
  label?: string;
  t?: number;
}

export const SpeedCompareScene: React.FC<SpeedCompareSceneProps> = ({
  opacity = 1, accentColor = '#ef4444', t = 0,
  label = 'Same time — the faster one goes further!',
}) => {
  const cycle = loop(t, 5);               // a fresh race every 5s
  const startX = 110, endX = 700;
  const fastP = clamp(easeOut(cycle) * 1.0, 0, 1);
  const slowP = cycle * 0.4;              // tortoise barely gets going
  const fastX = lerp(startX, endX, fastP);
  const slowX = lerp(startX, endX, clamp(slowP, 0, 1));
  const fastWheel = (t / 0.3) * 360;
  const laneFastY = 150, laneSlowY = 320;

  return (
    <g opacity={opacity}>
      <rect x={0} y={0} width={800} height={450} fill="#eef6ff" />
      {/* start + finish lines */}
      <line x1={startX} y1={90} x2={startX} y2={400} stroke="#94a3b8" strokeWidth={3} strokeDasharray="6 6" />
      <line x1={endX} y1={90} x2={endX} y2={400} stroke="#22c55e" strokeWidth={3} />
      <text x={startX} y={80} textAnchor="middle" fill="#64748b" fontSize={12} fontWeight={700} fontFamily="Inter,sans-serif">Start</text>
      <text x={endX} y={80} textAnchor="middle" fill="#16a34a" fontSize={12} fontWeight={700} fontFamily="Inter,sans-serif">Finish</text>

      {/* FAST lane: car */}
      <g>
        <rect x={70} y={laneFastY - 2} width={660} height={50} rx={10} fill="#dbeafe" />
        <text x={84} y={laneFastY - 14} fill="#1d4ed8" fontSize={14} fontWeight={800} fontFamily="Inter,sans-serif">Fast 🚗</text>
        {/* distance covered bar */}
        <rect x={startX} y={laneFastY + 38} width={fastX - startX} height={6} rx={3} fill="#3b82f6" />
        <g transform={`translate(${fastX} ${laneFastY + 16}) scale(0.55)`}>{Car(0, 0, accentColor, fastWheel)}</g>
      </g>

      {/* SLOW lane: tortoise */}
      <g>
        <rect x={70} y={laneSlowY - 2} width={660} height={50} rx={10} fill="#dcfce7" />
        <text x={84} y={laneSlowY - 14} fill="#15803d" fontSize={14} fontWeight={800} fontFamily="Inter,sans-serif">Slow 🐢</text>
        <rect x={startX} y={laneSlowY + 38} width={slowX - startX} height={6} rx={3} fill="#22c55e" />
        <g transform={`translate(${slowX} ${laneSlowY + 14})`}>
          <ellipse cx={0} cy={18} rx={26} ry={5} fill="rgba(0,0,0,0.15)" />
          <ellipse cx={0} cy={4} rx={24} ry={16} fill="#166534" />
          <ellipse cx={0} cy={0} rx={18} ry={12} fill="#22c55e" />
          <circle cx={22} cy={2} r={8} fill="#16a34a" />
          <circle cx={25} cy={0} r={1.6} fill="#052e16" />
          {/* little legs paddling */}
          <line x1={-12} y1={14} x2={-16 + wobble(t, 0.3, 3)} y2={20} stroke="#166534" strokeWidth={4} strokeLinecap="round" />
          <line x1={10} y1={14} x2={14 - wobble(t, 0.3, 3)} y2={20} stroke="#166534" strokeWidth={4} strokeLinecap="round" />
        </g>
      </g>

      {/* a shared clock so it's clear the TIME is the same for both */}
      <g transform="translate(400 410)">
        <text x={0} y={0} textAnchor="middle" fill="#475569" fontSize={13} fontWeight={600} fontFamily="Inter,sans-serif">Same clock for both — {Math.floor(cycle * 5)}s</text>
      </g>

      <g transform="translate(400 36)">
        <rect x={-260} y={-22} width={520} height={40} rx={20} fill="rgba(255,255,255,0.9)" stroke="#bfdbfe" strokeWidth={2} />
        <text x={0} y={4} textAnchor="middle" fill="#0f172a" fontSize={16} fontWeight={700} fontFamily="Inter,sans-serif">{label}</text>
      </g>
    </g>
  );
};
